import { useEffect } from 'react';
import './DazzlingGleamAnimation.css';

/**
 * PROFESSIONAL DAZZLING GLEAM ANIMATION
 * Fairy-type special move with blinding light and sparkle effects
 * 
 * Key Features:
 * - Fairy-type special attack (80 base power, 100% accuracy)
 * - Hits all adjacent opponents in double/triple battles
 * - Brilliant light burst with fairy sparkles and rainbow effects
 * - Professional gaming company quality with magical timing
 * - Special move animation with light-based mechanics and dazzling visuals
 */
export default function DazzlingGleamAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.closest('.battle-container') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate light trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    // Elements to clean up
    const elements = [];

    // PHASE 1: Fairy Light Gathering (0-500ms)
    console.log('Dazzling Gleam Phase 1: Fairy Light Gathering');
    
    // Player light gathering with fairy aura
    playerEl.classList.add('dazzling-gleam-user-gathering');
    
    // Fairy light aura around player
    const fairyAura = document.createElement('div');
    fairyAura.className = 'dazzling-gleam-fairy-aura';
    fairyAura.style.left = `${playerX - 110}px`;
    fairyAura.style.top = `${playerY - 110}px`;
    fairyAura.style.width = '220px';
    fairyAura.style.height = '220px';
    container.appendChild(fairyAura);
    elements.push(fairyAura);

    // Fairy sparkle particles around player
    for (let i = 0; i < 20; i++) {
      const sparkleParticle = document.createElement('div');
      sparkleParticle.className = 'dazzling-gleam-sparkle-particle';
      const angle = (i / 20) * 360;
      const radius = 80 + (i % 5) * 15;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      sparkleParticle.style.left = `${particleX - 6}px`;
      sparkleParticle.style.top = `${particleY - 6}px`;
      sparkleParticle.style.animationDelay = `${i * 20}ms`;
      sparkleParticle.textContent = '✨';
      container.appendChild(sparkleParticle);
      elements.push(sparkleParticle);
    }

    // Rainbow light orbs gathering energy
    const rainbowColors = ['🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '🩷'];
    for (let i = 0; i < 7; i++) {
      const rainbowOrb = document.createElement('div');
      rainbowOrb.className = 'dazzling-gleam-rainbow-orb';
      const orbAngle = (i / 7) * 360;
      const orbRadius = 90;
      const orbX = playerX + Math.cos(orbAngle * Math.PI / 180) * orbRadius;
      const orbY = playerY + Math.sin(orbAngle * Math.PI / 180) * orbRadius;
      rainbowOrb.style.left = `${orbX - 8}px`;
      rainbowOrb.style.top = `${orbY - 8}px`;
      rainbowOrb.style.animationDelay = `${i * 60}ms`;
      rainbowOrb.textContent = rainbowColors[i];
      container.appendChild(rainbowOrb);
      elements.push(rainbowOrb);
    }

    // Light concentration core
    const lightCore = document.createElement('div');
    lightCore.className = 'dazzling-gleam-light-core';
    lightCore.style.left = `${playerX - 30}px`;
    lightCore.style.top = `${playerY - 30}px`;
    lightCore.style.width = '60px';
    lightCore.style.height = '60px';
    container.appendChild(lightCore);
    elements.push(lightCore);

    // PHASE 2: Brilliant Light Beam (500-1000ms)
    const phase2Timer = setTimeout(() => {
      console.log('Dazzling Gleam Phase 2: Brilliant Light Beam');
      
      // Player light beam emission
      playerEl.classList.remove('dazzling-gleam-user-gathering');
      playerEl.classList.add('dazzling-gleam-user-beam');

      // Create targeted light rays instead of beam
      for (let i = 0; i < 8; i++) {
        const lightRay = document.createElement('div');
        lightRay.className = 'dazzling-gleam-light-ray';
        const rayProgress = (i + 1) / 8;
        const rayX = playerX + (dx * rayProgress * 0.8);
        const rayY = playerY + (dy * rayProgress * 0.8);
        lightRay.style.left = `${rayX - 15}px`;
        lightRay.style.top = `${rayY - 15}px`;
        lightRay.style.width = '30px';
        lightRay.style.height = '30px';
        lightRay.style.animationDelay = `${i * 40}ms`;
        container.appendChild(lightRay);
        elements.push(lightRay);
      }

      // Light beam sparkles
      for (let i = 0; i < 15; i++) {
        const beamSparkle = document.createElement('div');
        beamSparkle.className = 'dazzling-gleam-beam-sparkle';
        const progress = i / 15;
        const sparkleX = playerX + (dx * progress) + (Math.random() - 0.5) * 30;
        const sparkleY = playerY + (dy * progress) + (Math.random() - 0.5) * 20;
        beamSparkle.style.left = `${sparkleX - 4}px`;
        beamSparkle.style.top = `${sparkleY - 4}px`;
        beamSparkle.style.animationDelay = `${i * 25}ms`;
        beamSparkle.textContent = '⭐';
        container.appendChild(beamSparkle);
        elements.push(beamSparkle);
      }

      // Rainbow light trail
      for (let i = 0; i < 10; i++) {
        const trailTimer = setTimeout(() => {
          const rainbowTrail = document.createElement('div');
          rainbowTrail.className = 'dazzling-gleam-rainbow-trail';
          const trailProgress = i / 10;
          const trailX = playerX + (dx * trailProgress) + (Math.random() - 0.5) * 25;
          const trailY = playerY + (dy * trailProgress) + (Math.random() - 0.5) * 15;
          rainbowTrail.style.left = `${trailX - 6}px`;
          rainbowTrail.style.top = `${trailY - 6}px`;
          rainbowTrail.style.transform = `rotate(${angle + (Math.random() - 0.5) * 30}deg)`;
          container.appendChild(rainbowTrail);
          elements.push(rainbowTrail);
        }, i * 30);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Dazzling Impact Burst (1000-1500ms)
    const phase3Timer = setTimeout(() => {
      console.log('Dazzling Gleam Phase 3: Dazzling Impact Burst');
      
      // Opponent dazzled reaction
      opponentEl.classList.add('dazzling-gleam-opponent-dazzled');

      // Reset player position
      playerEl.classList.remove('dazzling-gleam-user-beam');
      playerEl.classList.add('dazzling-gleam-user-afterglow');

      // Massive dazzling explosion
      const dazzlingExplosion = document.createElement('div');
      dazzlingExplosion.className = 'dazzling-gleam-dazzling-explosion';
      dazzlingExplosion.style.left = `${opponentX - 150}px`;
      dazzlingExplosion.style.top = `${opponentY - 150}px`;
      dazzlingExplosion.style.width = '300px';
      dazzlingExplosion.style.height = '300px';
      container.appendChild(dazzlingExplosion);
      elements.push(dazzlingExplosion);

      // Fairy light burst particles
      for (let i = 0; i < 25; i++) {
        const burstParticle = document.createElement('div');
        burstParticle.className = 'dazzling-gleam-fairy-burst';
        const burstAngle = (i / 25) * 360;
        const burstRadius = 90 + Math.random() * 60;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        burstParticle.style.left = `${burstX - 6}px`;
        burstParticle.style.top = `${burstY - 6}px`;
        burstParticle.style.animationDelay = `${i * 15}ms`;
        burstParticle.style.setProperty('--burst-x', `${Math.cos(burstAngle * Math.PI / 180) * 40}px`);
        burstParticle.style.setProperty('--burst-y', `${Math.sin(burstAngle * Math.PI / 180) * 40}px`);
        burstParticle.textContent = '✨';
        container.appendChild(burstParticle);
        elements.push(burstParticle);
      }

      // Dazzling light rings
      for (let i = 0; i < 4; i++) {
        const ringTimer = setTimeout(() => {
          const lightRing = document.createElement('div');
          lightRing.className = 'dazzling-gleam-light-ring';
          lightRing.style.left = `${opponentX - 100}px`;
          lightRing.style.top = `${opponentY - 100}px`;
          lightRing.style.width = '200px';
          lightRing.style.height = '200px';
          lightRing.style.animationDelay = `${i * 80}ms`;
          container.appendChild(lightRing);
          elements.push(lightRing);
        }, i * 100);
        elements.push({ timer: ringTimer, type: 'timer' });
      }

      // Rainbow prism effects
      for (let i = 0; i < 12; i++) {
        const prismEffect = document.createElement('div');
        prismEffect.className = 'dazzling-gleam-prism-effect';
        const prismX = opponentX + (Math.random() - 0.5) * 120;
        const prismY = opponentY + (Math.random() - 0.5) * 100;
        prismEffect.style.left = `${prismX - 4}px`;
        prismEffect.style.top = `${prismY - 4}px`;
        prismEffect.style.animationDelay = `${i * 40}ms`;
        prismEffect.textContent = '🌈';
        container.appendChild(prismEffect);
        elements.push(prismEffect);
      }
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Sparkling Aftermath (1500-1800ms)
    const phase4Timer = setTimeout(() => {
      console.log('Dazzling Gleam Phase 4: Sparkling Aftermath');
      
      // Remove main effects
      playerEl.classList.remove('dazzling-gleam-user-afterglow');
      
      // Sparkling dissipation effect
      const sparklingDissipation = document.createElement('div');
      sparklingDissipation.className = 'dazzling-gleam-sparkling-dissipation';
      sparklingDissipation.style.left = `${opponentX - 100}px`;
      sparklingDissipation.style.top = `${opponentY - 100}px`;
      sparklingDissipation.style.width = '200px';
      sparklingDissipation.style.height = '200px';
      container.appendChild(sparklingDissipation);
      elements.push(sparklingDissipation);

      // Floating fairy lights
      for (let i = 0; i < 15; i++) {
        const fairyLight = document.createElement('div');
        fairyLight.className = 'dazzling-gleam-fairy-light';
        const lightX = opponentX + (Math.random() - 0.5) * 150;
        const lightY = opponentY + (Math.random() - 0.5) * 120;
        fairyLight.style.left = `${lightX - 4}px`;
        fairyLight.style.top = `${lightY - 4}px`;
        fairyLight.style.animationDelay = `${i * 30}ms`;
        fairyLight.textContent = '💫';
        container.appendChild(fairyLight);
        elements.push(fairyLight);
      }

      // Magical shimmer effect
      const magicalShimmer = document.createElement('div');
      magicalShimmer.className = 'dazzling-gleam-magical-shimmer';
      magicalShimmer.style.left = `${opponentX + 30}px`;
      magicalShimmer.style.top = `${opponentY - 40}px`;
      magicalShimmer.textContent = '✨💖✨';
      container.appendChild(magicalShimmer);
      elements.push(magicalShimmer);
    }, 1500);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Dazzling Gleam: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('dazzling-gleam-user-gathering', 'dazzling-gleam-user-beam', 'dazzling-gleam-user-afterglow');
      opponentEl.classList.remove('dazzling-gleam-opponent-dazzled');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1800);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('dazzling-gleam-user-gathering', 'dazzling-gleam-user-beam', 'dazzling-gleam-user-afterglow');
      opponentEl.classList.remove('dazzling-gleam-opponent-dazzled');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
DazzlingGleamAnimation.moveType = 'fairy';
